import React, { useState } from 'react';
import Cookies from 'js-cookie';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LinkIcon from '@mui/icons-material/Link';

const DRAFT_LIMIT = 5;

const SUPPORTED_SITES = [
    'AllRecipes', 'Food Network', 'NYT Cooking', 'Serious Eats',
    'Epicurious', 'Bon Appétit', 'BBC Good Food', 'Tasty',
];

const ImportFromUrlDialog = ({ open, onClose, onImport, currentDraftCount, existingDrafts = [] }) => {
    const [urlInput, setUrlInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);

    const allUrls = urlInput.split('\n').map(u => u.trim()).filter(Boolean);
    const slotsAvailable = DRAFT_LIMIT - currentDraftCount;
    const existingSourceUrls = new Set(existingDrafts.map(d => d.content?.source_url).filter(Boolean));
    const seenInInput = new Set();
    const uniqueUrls = allUrls.filter(u => {
        if (seenInInput.has(u)) return false;
        seenInInput.add(u);
        return true;
    });
    const duplicateUrls = new Set(uniqueUrls.filter(u => existingSourceUrls.has(u)));
    const newUrls = uniqueUrls.filter(u => !duplicateUrls.has(u));
    const urls = newUrls.slice(0, slotsAvailable);
    const overLimit = newUrls.length > slotsAvailable;

    const handleImport = async () => {
        const urlsToImport = urls;
        setLoading(true);
        setResults(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/recipes/import-url`, {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + Cookies.get('token'),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ urls: urlsToImport }),
            });
            const data = await res.json();
            setResults(data.results);
        } catch (e) {
            setResults([{ url: 'request', success: false, error: 'Network error — could not reach the server.' }]);
        } finally {
            setLoading(false);
        }
    };

    const successResults = results?.filter(r => r.success) || [];

    const handleDone = () => {
        if (successResults.length > 0) {
            onImport(successResults.map(r => r.data));
        }
        handleClose();
    };

    const handleClose = () => {
        setUrlInput('');
        setResults(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><LinkIcon sx={{ color: 'primary.main' }} />Import from URL</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Paste one recipe URL per line. Each URL will be saved as a draft.
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ mb: 2, display: 'block' }}>
                    Works with: {SUPPORTED_SITES.join(', ')}, and many more.
                </Typography>

                {!results && (
                    <>
                        {slotsAvailable === 0 ? (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                You've reached the 5-draft limit. Delete a draft before importing.
                            </Alert>
                        ) : (
                            <>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={8}
                                    placeholder="https://www.allrecipes.com/recipe/..."
                                    value={urlInput}
                                    onChange={e => setUrlInput(e.target.value)}
                                    disabled={loading}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 1 }}>
                                    <Box>
                                        {(duplicateUrls.size > 0 || allUrls.length !== uniqueUrls.length) && (
                                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                                                {[
                                                    allUrls.length !== uniqueUrls.length && `${allUrls.length - uniqueUrls.length} duplicate${allUrls.length - uniqueUrls.length !== 1 ? 's' : ''} in list`,
                                                    duplicateUrls.size > 0 && `${duplicateUrls.size} already a draft`,
                                                ].filter(Boolean).join(' · ')} — skipping.
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color={overLimit ? 'warning.main' : 'text.disabled'}>
                                            {overLimit
                                                ? `Draft limit reached — only the first ${slotsAvailable} new URL${slotsAvailable !== 1 ? 's' : ''} will be imported.`
                                                : `${slotsAvailable} draft slot${slotsAvailable !== 1 ? 's' : ''} available`
                                            }
                                        </Typography>
                                    </Box>
                                    {allUrls.length > 0 && (
                                        <Typography variant="caption" color={overLimit ? 'warning.main' : 'text.secondary'} sx={{ flexShrink: 0, ml: 1 }}>
                                            {Math.min(newUrls.length, slotsAvailable)}/{slotsAvailable}
                                        </Typography>
                                    )}
                                </Box>
                            </>
                        )}
                    </>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">Scraping recipes…</Typography>
                    </Box>
                )}

                {results && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {successResults.length} of {results.length} imported successfully.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {results.map((r, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    {r.success
                                        ? <CheckCircleIcon fontSize="small" color="success" sx={{ mt: 0.25, flexShrink: 0 }} />
                                        : <ErrorIcon fontSize="small" color="error" sx={{ mt: 0.25, flexShrink: 0 }} />
                                    }
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                            {r.success ? r.data.title || r.url : r.url}
                                        </Typography>
                                        {!r.success && (
                                            <Typography variant="caption" color="error.main">{r.error}</Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} color="inherit">Cancel</Button>
                {!results ? (
                    <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={urls.length === 0 || loading || slotsAvailable === 0}
                    >
                        Import
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleDone}
                        disabled={successResults.length === 0}
                    >
                        {successResults.length > 0 ? `Load ${successResults.length > 1 ? 'First' : ''} Recipe` : 'Close'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ImportFromUrlDialog;
